import * as React from "react";
import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import User, { NULL_USER } from "./models/User";
import {
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import InfoDialog from "./components/InfoDialog";
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {
  fetchSessionCatalogs,
  fetchSessionInfo,
  http,
  refreshSessionRequestForQuotes,
} from "./api/ProcurementClient";
import { SessionInfo } from "./models/SessionInfo";
import Customer from "./models/Customer";
import Vendor from "./models/Vendor";
import Manufacturer from "./models/Manufacturer";
import Rfq from "./models/Rfq";
import { CatalogType, CatalogTypeCategory } from "./models/CatalogType";
import { registerShoppingTabPersistenceListener } from "./shoppingTabPersistence";
import {
  registerShoppingPagePreviewBridge,
  registerShoppingPagePullListener,
} from "./shoppingPagePull";
import { registerOrderPageBridge } from "./orderPagePull";
import { setCopyFeedbackHandler } from "./utils/copyTextToClipboard";
import {
  RCS_ADMIN_FEE_TYPE,
  setDefaultTaxRatePercent,
  setRcsAdminFee,
} from "./PricingUtils";
import {
  mergeLiteSessionCatalogs,
  readCachedSessionCatalogs,
  writeCachedSessionCatalogs,
} from "./utils/sessionCatalogCache";

export interface AppContextType extends SessionInfo {
  setSnackbarMsg: (msg: string | undefined) => void;
  setSnackbarErrorMsg: (msg: string | undefined) => void;
  setStatusMsg: (msg: string | undefined) => void;
  setShowGlassPane: (show: boolean) => void;
  setUser: (user: User) => void;
  setUsers: (users: User[]) => void;
  setCustomers: (customers: Customer[] | ((prev: Customer[]) => Customer[])) => void;
  setRfqs: (requestForQuotes: Rfq[] | ((prev: Rfq[]) => Rfq[])) => void;
  setVendors: (vendors: Vendor[] | ((prev: Vendor[]) => Vendor[])) => void;
  setManufacturers: (manufacturers: Manufacturer[]) => void;
  setPartNumberTypes: (types: CatalogType[]) => void;
  setFeeTypes: (types: CatalogType[]) => void;
  setNoteTypes: (types: CatalogType[]) => void;
  setDeliveryMethods: (types: CatalogType[]) => void;
  setDeliverySpeeds: (types: CatalogType[]) => void;
  setRfqStatuses: (types: CatalogType[]) => void;
  upsertCatalogType: (type: CatalogType) => void;
  removeCatalogType: (id: number, category: CatalogTypeCategory) => void;
  viewArchivedEmails: boolean;
  setViewArchivedEmails: (viewArchived: boolean) => void;
  viewArchivedRfqs: boolean;
  setViewArchivedRfqs: (viewArchived: boolean) => void;
}

const startOfMonth = new Date();
startOfMonth.setDate(1);
startOfMonth.setHours(0, 0, 0, 0);

const endOfMonth = new Date(
  startOfMonth.getFullYear(),
  startOfMonth.getMonth() + 1,
  0
);
endOfMonth.setHours(23, 59, 59, 999);

export const INITIAL_APP_CONTEXT: AppContextType = {
  users: [],
  user: NULL_USER,
  customers: [],
  requestForQuotes: [],
  vendors: [],
  manufacturers: [],
  partNumberTypes: [],
  feeTypes: [],
  noteTypes: [],
  deliveryMethods: [],
  deliverySpeeds: [],
  rfqStatuses: [],
  setRfqs: () => {},
  setVendors: () => {},
  setManufacturers: () => {},
  setPartNumberTypes: () => {},
  setFeeTypes: () => {},
  setNoteTypes: () => {},
  setDeliveryMethods: () => {},
  setDeliverySpeeds: () => {},
  setRfqStatuses: () => {},
  upsertCatalogType: () => {},
  removeCatalogType: () => {},
  setSnackbarMsg: () => {},
  setSnackbarErrorMsg: () => {},
  setStatusMsg: () => {},
  setShowGlassPane: () => {},
  setCustomers: () => {},
  setUser: () => {},
  setUsers: () => {},
  viewArchivedEmails: false,
  setViewArchivedEmails: () => {},
  viewArchivedRfqs: false,
  setViewArchivedRfqs: () => {},
};

export const AppContext = createContext<AppContextType>(INITIAL_APP_CONTEXT);

const AppContextProvider: React.FC<React.PropsWithChildren> = (props) => {
  const [user, setUser] = useState<User>(NULL_USER);
  const [users, setUsers] = useState<User[]>([]);
  const [statusMsg, setStatusMsg] = useState<string | undefined>();
  const [snackbarMsg, setSnackbarMsg] = useState<string | undefined>();
  const [snackbarErrorMsg, setSnackbarErrorMsg] = useState<
    string | undefined
  >();
  const [requestForQuotes, setRfqs] = useState<Rfq[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [partNumberTypes, setPartNumberTypes] = useState<CatalogType[]>([]);
  const [feeTypes, setFeeTypes] = useState<CatalogType[]>([]);
  useEffect(() => {
    setDefaultTaxRatePercent(
      feeTypes.find((t) => t.value === "TAX")?.ratePercent ?? null
    );
    const adminFee = feeTypes.find((t) => t.value === RCS_ADMIN_FEE_TYPE);
    setRcsAdminFee(adminFee?.ratePercent ?? null, adminFee?.flatAmount ?? null);
  }, [feeTypes]);
  const [noteTypes, setNoteTypes] = useState<CatalogType[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<CatalogType[]>([]);
  const [deliverySpeeds, setDeliverySpeeds] = useState<CatalogType[]>([]);
  const [rfqStatuses, setRfqStatuses] = useState<CatalogType[]>([]);
  const [showGlassPane, setShowGlassPane] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [viewArchivedEmails, setViewArchivedEmails] = useState(false);
  const [viewArchivedRfqs, setViewArchivedRfqs] = useState(false);

  // Must be stable: RfqPanel (and others) put setRfqs in effect deps.
  // A new function identity each render re-triggers getQuote → setRfqs → loop.
  const handleSetRfqs = useCallback(
    (value: Rfq[] | ((prev: Rfq[]) => Rfq[])) => {
      setRfqs(value);
    },
    []
  );

  const upsertCatalogType = (type: CatalogType) => {
    const upsert = (prev: CatalogType[]) => {
      const without = prev.filter((t) => t.id !== type.id);
      return [...without, type].sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        return a.value.localeCompare(b.value);
      });
    };
    if (type.category === CatalogTypeCategory.PART_NUMBER) {
      setPartNumberTypes(upsert);
    } else if (type.category === CatalogTypeCategory.FEE) {
      setFeeTypes(upsert);
    } else if (type.category === CatalogTypeCategory.NOTE) {
      setNoteTypes(upsert);
    } else if (type.category === CatalogTypeCategory.DELIVERY_METHOD) {
      setDeliveryMethods(upsert);
    } else if (type.category === CatalogTypeCategory.DELIVERY_SPEED) {
      setDeliverySpeeds(upsert);
    } else if (type.category === CatalogTypeCategory.RFQ_STATUS) {
      setRfqStatuses(upsert);
    }
  };

  const removeCatalogType = (id: number, category: CatalogTypeCategory) => {
    if (category === CatalogTypeCategory.PART_NUMBER) {
      setPartNumberTypes((prev) => prev.filter((t) => t.id !== id));
    } else if (category === CatalogTypeCategory.FEE) {
      setFeeTypes((prev) => prev.filter((t) => t.id !== id));
    } else if (category === CatalogTypeCategory.NOTE) {
      setNoteTypes((prev) => prev.filter((t) => t.id !== id));
    } else if (category === CatalogTypeCategory.DELIVERY_METHOD) {
      setDeliveryMethods((prev) => prev.filter((t) => t.id !== id));
    } else if (category === CatalogTypeCategory.DELIVERY_SPEED) {
      setDeliverySpeeds((prev) => prev.filter((t) => t.id !== id));
    } else if (category === CatalogTypeCategory.RFQ_STATUS) {
      setRfqStatuses((prev) => prev.filter((t) => t.id !== id));
    }
  };

  const requestOkInterceptor = (
    config:
      | InternalAxiosRequestConfig<unknown>
      | Promise<InternalAxiosRequestConfig<unknown>>
  ) => {
    const jwtToken = localStorage.getItem("jwtToken");

    // Opt-in only: background fetches stay silent. Intentional actions that
    // need a blocking overlay set `showGlassPane: true` on the request config.
    // (`skipGlassPane` is still honored for older callers.)
    const cfg = config as InternalAxiosRequestConfig & {
      showGlassPane?: boolean;
      skipGlassPane?: boolean;
    };
    if (cfg.showGlassPane && !cfg.skipGlassPane) {
      setShowGlassPane(true);
    }

    console.log(
      jwtToken ? "using jwtToken = " + jwtToken : "no jwtToken found"
    );

    if (jwtToken) {
      (config as InternalAxiosRequestConfig).headers.Authorization =
        "Bearer " + jwtToken;
    }
    return config;
  };

  const requestErrorInterceptor = (error: unknown) => {
    setShowGlassPane(false);
    return Promise.reject(error);
  };

  const responseOkInterceptor = (
    response:
      | AxiosResponse<unknown, unknown>
      | Promise<AxiosResponse<unknown, unknown>>
  ) => {
    setShowGlassPane(false);
    return response;
  };

  const responseErrorInterceptor = (error: AxiosError) => {
    setShowGlassPane(false);
    return Promise.reject(error);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarMsg(undefined);
    setSnackbarErrorMsg(undefined);
  };

  useEffect(() => {
    setCopyFeedbackHandler(() => setSnackbarMsg("Copied"));
    return () => setCopyFeedbackHandler(undefined);
  }, []);

  useEffect(() => {
    const responseInterceptor = http.interceptors.response.use(
      responseOkInterceptor,
      responseErrorInterceptor
    );

    const requestInterceptor = http.interceptors.request.use(
      requestOkInterceptor,
      requestErrorInterceptor
    );

    const initSession = async () => {
      try {
        const cachedCatalogs = readCachedSessionCatalogs();
        // Auth/enums and lean RFQ list in parallel — paint with rows when both settle.
        const [sessionResult, rfqResult] = await Promise.allSettled([
          fetchSessionInfo({
            includeCatalogs: false,
            includeRfqs: false,
          }),
          refreshSessionRequestForQuotes(),
        ]);
        if (sessionResult.status !== "fulfilled") {
          throw sessionResult.reason;
        }
        const sessionInfo = sessionResult.value;
        const requestForQuotes =
          rfqResult.status === "fulfilled" ? rfqResult.value : [];
        setUser(sessionInfo.user);
        setUsers(sessionInfo.users);
        setRfqs(requestForQuotes);
        setPartNumberTypes(sessionInfo.partNumberTypes ?? []);
        setFeeTypes(sessionInfo.feeTypes ?? []);
        setNoteTypes(sessionInfo.noteTypes ?? []);
        setDeliveryMethods(sessionInfo.deliveryMethods ?? []);
        setDeliverySpeeds(sessionInfo.deliverySpeeds ?? []);
        setRfqStatuses(sessionInfo.rfqStatuses ?? []);

        if (cachedCatalogs) {
          setCustomers(cachedCatalogs.customers);
          setVendors(cachedCatalogs.vendors);
          setManufacturers(cachedCatalogs.manufacturers);
        } else {
          setCustomers([]);
          setVendors([]);
          setManufacturers([]);
        }
        setIsInitialized(true);

        void fetchSessionCatalogs()
          .then((catalogs) => {
            startTransition(() => {
              setCustomers((prev) =>
                mergeLiteSessionCatalogs(
                  { customers: prev, vendors: [], manufacturers: [] },
                  catalogs
                ).customers
              );
              setVendors((prev) =>
                mergeLiteSessionCatalogs(
                  { customers: [], vendors: prev, manufacturers: [] },
                  catalogs
                ).vendors
              );
              setManufacturers(catalogs.manufacturers);
            });
            writeCachedSessionCatalogs(catalogs);
          })
          .catch(() => {
            /* keep cache / empty */
          });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        setIsInitialized(true);
      }
    };

    initSession();

    return () => {
      http.interceptors.request.eject(requestInterceptor);
      http.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (snackbarMsg) {
      setSnackbarErrorMsg(undefined);
    }
  }, [snackbarMsg]);

  useEffect(() => {
    if (snackbarErrorMsg) {
      setSnackbarMsg(undefined);
    }
  }, [snackbarErrorMsg]);

  useEffect(() => {
    return registerShoppingTabPersistenceListener(handleSetRfqs);
  }, []);

  useEffect(() => registerShoppingPagePullListener(), []);

  const requestForQuotesRef = useRef(requestForQuotes);
  requestForQuotesRef.current = requestForQuotes;
  useEffect(
    () => registerShoppingPagePreviewBridge(() => requestForQuotesRef.current),
    []
  );
  useEffect(() => registerOrderPageBridge(), []);

  if (!isInitialized) {
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  return (
    <AppContext.Provider
      value={{
        user,
        users,
        customers,
        requestForQuotes,
        vendors,
        manufacturers,
        partNumberTypes,
        feeTypes,
        noteTypes,
        deliveryMethods,
        deliverySpeeds,
        rfqStatuses,
        setRfqs: handleSetRfqs,
        setVendors,
        setManufacturers,
        setPartNumberTypes,
        setFeeTypes,
        setNoteTypes,
        setDeliveryMethods,
        setDeliverySpeeds,
        setRfqStatuses,
        upsertCatalogType,
        removeCatalogType,
        setStatusMsg,
        setSnackbarMsg,
        setSnackbarErrorMsg,
        setShowGlassPane,
        setUsers,
        setUser,
        setCustomers,
        viewArchivedEmails,
        setViewArchivedEmails,
        viewArchivedRfqs,
        setViewArchivedRfqs,
      }}
    >
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          paddingLeft: "0px",
          paddingRight: "0px",
        }}
      >
        {props.children}
        {!statusMsg ? (
          <></>
        ) : (
          <InfoDialog msg={statusMsg} onOk={() => setStatusMsg(undefined)} />
        )}
        <Backdrop
          sx={{
            zIndex: (theme) =>
              Math.max.apply(Math, Object.values(theme.zIndex)) + 1,
          }}
          open={showGlassPane}
          onClick={() => {}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={snackbarMsg || snackbarErrorMsg ? true : false}
          autoHideDuration={snackbarErrorMsg ? 3000 : 2000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            severity={snackbarErrorMsg ? "error" : "success"}
            onClose={handleCloseSnackbar}
          >
            {snackbarMsg || snackbarErrorMsg || undefined}
          </Alert>
        </Snackbar>
      </Box>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
